<template>
  <div class="page-card">
    <div class="page-header">
      <h2 class="page-title">艺人IP档案审核详情</h2>
      <el-button @click="goBack">返回列表</el-button>
    </div>

    <el-descriptions :column="2" border>
      <el-descriptions-item label="艺人ID">{{ base.artistId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="艺人">{{ base.artistName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="状态">
        <el-tag :type="statusTagType(currentStatus)">{{ statusText(currentStatus) }}</el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="驳回/补充原因">{{ base.rejectReason || '-' }}</el-descriptions-item>
    </el-descriptions>

    <el-divider>接单数据</el-divider>
    <div class="stat-grid">
      <div v-for="item in orderStatItems" :key="item.label" class="stat-item">
        <div class="stat-value">{{ item.value }}</div>
        <div class="stat-label">{{ item.label }}</div>
      </div>
    </div>

    <el-divider>提交记录</el-divider>
    <el-table :data="records" border>
      <el-table-column label="类型" width="110">
        <template slot-scope="{ row }">{{ row.recordTypeText || recordTypeText(row.recordType) }}</template>
      </el-table-column>
      <el-table-column prop="recordTitle" label="标题" min-width="160" />
      <el-table-column prop="eventDate" label="日期" width="120" />
      <el-table-column prop="partnerName" label="机构/项目方" min-width="150" />
      <el-table-column prop="roleName" label="角色/身份" min-width="130" />
      <el-table-column prop="recordDesc" label="描述" min-width="220" show-overflow-tooltip />
      <el-table-column prop="achievementDesc" label="成果" min-width="180" show-overflow-tooltip />
      <el-table-column label="附件" min-width="220">
        <template slot-scope="{ row }">
          <div v-if="attachmentItems(row).length" class="attachment-grid">
            <div v-for="(item, index) in attachmentItems(row)" :key="`${item.mediaUrl}-${index}`" class="attachment-item">
              <el-image
                v-if="isImageAttachment(item)"
                :src="item.mediaUrl"
                :preview-src-list="imagePreviewList(row)"
                fit="cover"
                class="attachment-thumb"
              >
                <div slot="error" class="attachment-error">图片加载失败</div>
              </el-image>
              <div v-else-if="isVideoAttachment(item)" class="attachment-video-card" @click="openVideoPreview(item)">
                <video class="attachment-video" :src="item.mediaUrl" :poster="item.coverUrl || ''" muted preload="metadata" />
                <div class="attachment-video-mask">
                  <span class="attachment-video-play">▶</span>
                  <span>视频</span>
                </div>
              </div>
              <el-link v-else :href="item.mediaUrl" target="_blank" type="primary" class="attachment-file">查看附件</el-link>
            </div>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>

    <el-divider>审核记录</el-divider>
    <el-table :data="detail.auditHistory || []" border>
      <el-table-column label="状态">
        <template slot-scope="{ row }">{{ statusText(row.status) }}</template>
      </el-table-column>
      <el-table-column prop="reason" label="原因" />
      <el-table-column prop="remark" label="备注" />
      <el-table-column prop="operatorName" label="处理人" />
      <el-table-column prop="operatedAt" label="处理时间" />
    </el-table>

    <div v-if="canOperate" class="action-bar">
      <el-button type="success" :loading="submitting" @click="approve">审核通过</el-button>
      <el-button type="danger" :loading="submitting" @click="openRejectDialog">驳回</el-button>
    </div>

    <el-dialog title="驳回原因" :visible.sync="rejectDialogVisible" width="480px" @closed="rejectReason = ''">
      <el-input v-model.trim="rejectReason" type="textarea" :rows="4" placeholder="请输入驳回原因" />
      <span slot="footer">
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="reject">确认驳回</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  approveArtistStarProfile,
  getArtistStarProfileAuditDetail,
  rejectArtistStarProfile
} from '../../api/audit'
import { navigateWithFallback } from '../../utils/admin-navigation'
import { isImageMedia, isVideoMedia, normalizeMediaItem } from '../../utils/format'

const AUDIT_STATUS_PENDING = 1
const AUDIT_STATUS_APPROVED = 2
const AUDIT_STATUS_REJECTED = 3
const AUDIT_STATUS_SUPPLEMENT = 4

export default {
  name: 'ArtistStarProfileDetailPage',
  data() {
    return {
      detail: {},
      rejectDialogVisible: false,
      videoPreviewVisible: false,
      previewVideo: {},
      rejectReason: '',
      submitting: false
    }
  },
  computed: {
    base() {
      return this.detail.baseInfo || {}
    },
    records() {
      return this.base.records || []
    },
    currentStatus() {
      return Number(this.base.status || 0)
    },
    canOperate() {
      return this.currentStatus === AUDIT_STATUS_PENDING
    },
    orderStats() {
      return this.base.orderStats || {}
    },
    orderStatItems() {
      return [
        { label: '累计接单', value: this.orderStats.totalOrderCount || 0 },
        { label: '进行中', value: this.orderStats.inProgressOrderCount || 0 },
        { label: '已完成', value: this.orderStats.completedOrderCount || 0 },
        { label: '已结算', value: this.orderStats.settledOrderCount || 0 },
        { label: '异常关闭', value: this.orderStats.closedOrderCount || 0 }
      ]
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.detail = await getArtistStarProfileAuditDetail(this.$route.params.id)
    },
    recordTypeText(type) {
      if (Number(type) === 1) return '成长记录'
      if (Number(type) === 2) return '获奖'
      if (Number(type) === 3) return '合作经历'
      return '其他'
    },
    statusText(status) {
      if (status === AUDIT_STATUS_PENDING) return '待审核'
      if (status === AUDIT_STATUS_APPROVED) return '已通过'
      if (status === AUDIT_STATUS_REJECTED) return '已驳回'
      if (status === AUDIT_STATUS_SUPPLEMENT) return '待补充资料'
      return status == null ? '-' : String(status)
    },
    statusTagType(status) {
      if (status === AUDIT_STATUS_PENDING) return 'warning'
      if (status === AUDIT_STATUS_APPROVED) return 'success'
      if (status === AUDIT_STATUS_REJECTED) return 'danger'
      if (status === AUDIT_STATUS_SUPPLEMENT) return 'info'
      return 'info'
    },
    normalizeAttachment(item) {
      if (!item) return null
      return normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item)
    },
    attachmentItems(row) {
      return ((row && row.attachments) || [])
        .map(item => this.normalizeAttachment(item))
        .filter(Boolean)
    },
    imagePreviewList(row) {
      return this.attachmentItems(row)
        .filter(item => this.isImageAttachment(item))
        .map(item => item.mediaUrl)
    },
    isImageAttachment(item) {
      return isImageMedia(item)
    },
    isVideoAttachment(item) {
      return isVideoMedia(item)
    },
    openVideoPreview(item) {
      this.previewVideo = {
        mediaUrl: item.mediaUrl || '',
        coverUrl: item.coverUrl || ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    },
    goBack() {
      const query = this.$route.query || {}
      navigateWithFallback(this, {
        path: '/audits',
        query: {
          type: query.fromType || 'artist-star-profile',
          status: query.fromStatus || '1',
          page: query.fromPage || '1'
        }
      })
    },
    async approve() {
      try {
        await MessageBox.confirm('确认审核通过该艺人IP档案吗？', '审核确认', {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      this.submitting = true
      try {
        await approveArtistStarProfile(this.$route.params.id, { remark: '审核通过' })
        this.$message.success('操作成功')
        this.goBack()
      } finally {
        this.submitting = false
      }
    },
    openRejectDialog() {
      this.rejectDialogVisible = true
    },
    async reject() {
      if (!this.rejectReason) {
        this.$message.warning('请输入驳回原因')
        return
      }
      this.submitting = true
      try {
        await rejectArtistStarProfile(this.$route.params.id, { reason: this.rejectReason, remark: this.rejectReason })
        this.$message.success('操作成功')
        this.goBack()
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.stat-item {
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  margin-top: 6px;
  color: #909399;
}

.attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.attachment-item {
  width: 64px;
  height: 64px;
}

.attachment-thumb,
.attachment-video-card {
  width: 64px;
  height: 64px;
  border-radius: 6px;
}

.attachment-thumb {
  background: #f5f7fa;
}

.attachment-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  background: #f5f7fa;
}

.attachment-video-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #111827;
}

.attachment-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.attachment-video-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  font-size: 12px;
  background: rgba(17, 24, 39, 0.42);
}

.attachment-video-play {
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  text-align: center;
  background: rgba(255, 255, 255, 0.22);
}

.attachment-file {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f5f7fa;
}

.video-preview-wrap {
  display: flex;
  justify-content: center;
}

.preview-video-player {
  width: 100%;
  max-height: 72vh;
  border-radius: 10px;
  background: #111827;
}

.action-bar {
  margin-top: 20px;
}
</style>
